import React, { useEffect, useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
  CircularProgress,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import { get, groupBy, isEmpty, find, orderBy } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { Box } from 'components';
import { errorMessageHandler } from 'common/utils/helpers';
import { getAllInvestmentDocuments } from 'services';
import { RootState } from 'redux-modules/store/rootState';

import styles from './styles';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.grey[300],
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    borderBottom: 0,
  },
  body: {
    fontSize: 13,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.grey[50],
    borderBottom: 0,
  },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {},
}))(TableRow);

const Documents = () => {
  const classes = styles();

  const { investor } = useSelector(({ Investor }: RootState) => Investor);
  const { dealList } = useSelector(({ Deals }: RootState) => Deals);

  const { investorKyc, investmentDetail } = investor;

  const [documentRes, setDocumentRes] = useState<any>([]);
  const [documentList, setDocumentList] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const getDocumentName = (document: any) => {
    return get(document, 'documentName') === 'WIRE_RECEIPT'
      ? 'Wire Receipt_Drawdown'
      : get(document, 'documentName') === 'WIRE_INSTRUCTION'
      ? `Bank Account details_${get(document, 'signatoryLegalName')}`
      : get(document, 'documentName') === 'Docusign_Document'
      ? 'Subscription Agreements_Signed'
      : get(document, 'documentName') === 'RCVD_DIST_BANK_DETAILS'
      ? `Bank Account details`
      : get(document, 'documentName') === 'NAV_REPORT'
      ? `${get(
          find(dealList, { id: get(document, 'spv') }),
          'deal',
        )}_NAV Report_${moment(get(document, 'date')).format('YYYY-MM-DD')}`
      : get(document, 'filename');
  };

  const getInvestorDocumentList = async () => {
    try {
      setIsLoading(true);
      const response: any = await getAllInvestmentDocuments();
      setDocumentRes(response);
      const formattedDocuments = formatDocumentResponse(response);
      setDocumentList(formattedDocuments);
      setIsLoading(false);
    } catch (err) {
      const message: string = errorMessageHandler(err);
      setIsLoading(false);
      setErrorMessage(message);
    }
  };

  const formatDocumentResponse = (documentArray: any) => {
    try {
      documentArray = orderBy(documentArray, ['uploadedAt'], ['desc']);
      const newResponse: any = documentArray.map((document: any) => {
        const investmentData = investmentDetail.find(
          (investment: any) => investment.id === document.investmentDetailId,
        );
        if (!isEmpty(investmentData)) {
          const kycData = investorKyc.find(
            (kyc: any) => kyc.id === investmentData.kycId,
          );
          return {
            ...document,
            spv: get(investmentData, 'spv'),
            signatoryLegalName: get(kycData, 'signatoryLegalName'),
          };
        }
      });
      const groupedDocuments = groupBy(
        newResponse.filter((x: any) => x),
        'spv',
      );
      return groupedDocuments || {};
    } catch (err) {
      const message: string = errorMessageHandler(err);
      setErrorMessage(message);
    }
  };

  useEffect(() => {
    getInvestorDocumentList();
  }, []);

  useEffect(() => {
    if (!isEmpty(investmentDetail) && isEmpty(documentList)) {
      const formattedDocuments = formatDocumentResponse(documentRes);
      setDocumentList(formattedDocuments);
    }
  }, [investmentDetail, documentRes]);

  return (
    <Box className={classes.documentBox}>
      {isLoading ? (
        <Box className={classes.loader}>
          <CircularProgress color="inherit" size={40} />
        </Box>
      ) : (
        <>
          {errorMessage ? (
            <Typography variant="body1" className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          ) : (
            <>
              {isEmpty(documentList) && (
                <Typography className={classes.documentHeadText}>
                  You did not uploaded any document.
                  {/* All your documents orgnized in one place */}
                </Typography>
              )}
              {!isEmpty(documentList) && (
                <>
                  {Object.keys(documentList).map((spv: string) => (
                    <Accordion className={classes.accordionBox} key={spv}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                      >
                        <Typography className={classes.accoheading}>
                          SPV {get(find(dealList, { id: spv }), 'deal')}{' '}
                          Documents
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table
                            className={classes.table}
                            aria-label="customized table"
                          >
                            <TableHead>
                              <TableRow>
                                <StyledTableCell
                                  align="left"
                                  className={classes.documentNameText}
                                >
                                  Document Name
                                </StyledTableCell>
                                <StyledTableCell align="left">
                                  Added on
                                </StyledTableCell>
                                <StyledTableCell
                                  align="right"
                                  className={classes.downloadIconCell}
                                >
                                  Actions
                                </StyledTableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {documentList[spv].map((row: any) => (
                                <StyledTableRow key={row.id}>
                                  <StyledTableCell align="left">
                                    {getDocumentName(row)}
                                  </StyledTableCell>
                                  <StyledTableCell align="left">
                                    {moment(get(row, 'uploadedAt')).format(
                                      'DD/MM/YYYY',
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell
                                    align="right"
                                    className={classes.downloadIconCell}
                                  >
                                    <a
                                      href={get(row, 'documentUrl')}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <GetAppIcon
                                        className={classes.downloadIcon}
                                      />
                                    </a>
                                  </StyledTableCell>
                                </StyledTableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};
export default Documents;
